import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Avatar,
  Chip,
  IconButton,
  CircularProgress,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  alpha,
} from '@mui/material';
import {
  Close as CloseIcon,
  FavoriteRounded as HeartIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  DeleteOutline as DeleteIcon,
  BarChartOutlined as ReportIcon,
} from '@mui/icons-material';
import AuthContext from '../context/AuthContext';

const RelationshipTimeline = ({ open, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { backendUrl, authTokens } = useContext(AuthContext);
  const [relationships, setRelationships] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [relationshipToDelete, setRelationshipToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (open) {
      fetchAllRelationships();
    }
  }, [open]);

  const fetchAllRelationships = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${backendUrl}api/all-relationships/`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authTokens?.access}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch relationships');
      }

      const data = await response.json();
      // Sort relationships: active first, then by start date (most recent first)
      const sortedData = data.sort((a, b) => {
        // Active relationships always come first
        if (a.active && !b.active) return -1;
        if (!a.active && b.active) return 1;
        // If both are active or both are inactive, sort by start date (most recent first)
        return new Date(b.date_started) - new Date(a.date_started);
      });
      setRelationships(sortedData);
    } catch (error) {
      console.error('Error fetching relationships:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Present';
    return new Date(dateString).toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const calculateDuration = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = endDate ? new Date(endDate) : new Date();
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 30) {
      return `${diffDays} days`;
    } else if (diffDays < 365) {
      const months = Math.floor(diffDays / 30);
      return `${months} month${months !== 1 ? 's' : ''}`;
    } else {
      const years = Math.floor(diffDays / 365);
      const remainingMonths = Math.floor((diffDays % 365) / 30);
      if (remainingMonths === 0) {
        return `${years} year${years !== 1 ? 's' : ''}`;
      }
      return `${years}y ${remainingMonths}m`;
    }
  };

  const handleDeleteClick = (e, relationship) => {
    e.stopPropagation(); // Prevent card click navigation
    setRelationshipToDelete(relationship);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!relationshipToDelete) return;

    setDeleting(true);
    try {
      const response = await fetch(`${backendUrl}api/relationship/`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authTokens?.access}`,
        },
        body: JSON.stringify({
          relationship_id: relationshipToDelete.relationship_id,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to delete relationship');
      }

      // Remove the deleted relationship from the list
      setRelationships(relationships.filter(
        r => r.relationship_id !== relationshipToDelete.relationship_id
      ));

      setDeleteConfirmOpen(false);
      setRelationshipToDelete(null);
    } catch (error) {
      console.error('Error deleting relationship:', error);
      alert('Failed to delete relationship. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setRelationshipToDelete(null);
  };

  const TimelineItem = ({ relationship, index, isLast }) => {
    const isActive = relationship.active;
    const startDate = formatDate(relationship.date_started);
    const endDate = formatDate(relationship.date_ended);
    const duration = calculateDuration(relationship.date_started, relationship.date_ended);

    const handleClick = () => {
      onClose();
      if (isActive) {
        navigate('/relationship');
      } else {
        navigate(`/relationship?id=${relationship.relationship_id}`);
      }
    };

    const handleReportClick = (e) => {
      e.stopPropagation();
      onClose();
      navigate(`/statistics?relationship_id=${relationship.relationship_id}`);
    };

    return (
      <Box sx={{ position: 'relative', pb: isLast ? 0 : 4 }}>
        {/* Timeline Line */}
        {!isLast && (
          <Box
            sx={{
              position: 'absolute',
              left: 28,
              top: 56,
              width: 2,
              height: 'calc(100% - 40px)',
              background: alpha(theme.palette.primary.main, 0.2),
              zIndex: 0,
            }}
          />
        )}

        {/* Timeline Node */}
        <Box
          sx={{
            position: 'absolute',
            left: 20,
            top: 20,
            width: 16,
            height: 16,
            borderRadius: '50%',
            backgroundColor: isActive ? theme.palette.error.main : theme.palette.primary.main,
            border: `3px solid ${theme.palette.background.paper}`,
            boxShadow: `0 0 0 2px ${isActive ? theme.palette.error.main : theme.palette.primary.main}`,
            zIndex: 1,
          }}
        />

        {/* Content Card */}
        <Card
          onClick={handleClick}
          sx={{
            ml: { xs: 5, sm: 6 },
            cursor: 'pointer',
            background: theme.palette.background.paper,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: theme.shadows[8],
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            }
          }}
        >
          <CardContent sx={{ p: { xs: 2, md: 3 }, '&:last-child': { pb: { xs: 2, md: 3 } } }}>
            {/* Top row: avatar + name/email */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
              <Avatar
                src={relationship.relationship_picture ? `${backendUrl}${relationship.relationship_picture}` : null}
                sx={{
                  width: { xs: 40, sm: 48 },
                  height: { xs: 40, sm: 48 },
                  mr: 1.5,
                  flexShrink: 0,
                  border: `2px solid ${isActive ? theme.palette.error.main : theme.palette.primary.main}`,
                }}
              >
                💕
              </Avatar>
              <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                  <PersonIcon fontSize="small" sx={{ color: theme.palette.text.secondary, flexShrink: 0 }} />
                  <Typography
                    variant="subtitle1"
                    sx={{
                      color: theme.palette.text.primary,
                      fontWeight: 600,
                      lineHeight: 1.3,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: '100%',
                    }}
                  >
                    {relationship.partner_name}
                  </Typography>
                  {isActive && (
                    <Chip
                      label="Current"
                      size="small"
                      color="error"
                      icon={<HeartIcon />}
                      sx={{ fontSize: '0.7rem', height: 22 }}
                    />
                  )}
                </Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mt: 0.25,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  }}
                >
                  {relationship.partner_email}
                </Typography>
              </Box>
            </Box>

            {/* Action buttons row */}
            <Box sx={{ display: 'flex', gap: 1, mb: 1.5, justifyContent: 'flex-end' }}>
              {!isActive && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<ReportIcon fontSize="small" />}
                  onClick={handleReportClick}
                  sx={{
                    fontSize: '0.75rem',
                    textTransform: 'none',
                    borderRadius: 1.5,
                    px: 1.25,
                    py: 0.5,
                    borderColor: theme.palette.primary.main,
                    color: theme.palette.primary.main,
                    '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.08) },
                  }}
                >
                  Report
                </Button>
              )}
              <IconButton
                size="small"
                onClick={(e) => handleDeleteClick(e, relationship)}
                sx={{
                  color: theme.palette.error.main,
                  opacity: 0.7,
                  '&:hover': { opacity: 1, backgroundColor: alpha(theme.palette.error.main, 0.1) },
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* Date range row */}
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: relationship.comments ? 1.5 : 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CalendarIcon fontSize="small" color="primary" />
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                  {startDate} – {endDate}
                </Typography>
              </Box>
              <Chip
                label={duration}
                size="small"
                variant="outlined"
                color="primary"
                sx={{ fontSize: '0.7rem', height: 22 }}
              />
            </Box>

            {relationship.comments && (
              <Typography
                variant="body2"
                sx={{
                  fontStyle: 'italic',
                  color: theme.palette.text.secondary,
                  p: 1.25,
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                  borderRadius: 1,
                  borderLeft: `3px solid ${theme.palette.primary.main}`,
                }}
              >
                "{relationship.comments}"
              </Typography>
            )}
          </CardContent>
        </Card>
      </Box>
    );
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          background: theme.palette.background.paper,
        }
      }}
    >
      <DialogTitle sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        pb: 1,
        background: alpha(theme.palette.primary.main, 0.06),
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HeartIcon color="primary" />
          <Typography variant={isMobile ? 'h6' : 'h5'} component="div" sx={{ fontWeight: 600 }}>
            Relationship Timeline
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: { xs: 2, md: 4 } }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
            <CircularProgress size={40} />
          </Box>
        ) : relationships.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <HeartIcon sx={{ fontSize: 64, color: theme.palette.text.disabled, mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No relationships found
            </Typography>
            <Typography variant="body2" color="text.disabled">
              Start by creating your first relationship!
            </Typography>
          </Box>
        ) : (
          <Box sx={{ position: 'relative' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
              Your journey through {relationships.length} relationship{relationships.length !== 1 ? 's' : ''}
            </Typography>
            {relationships.map((relationship, index) => (
              <TimelineItem
                key={relationship.relationship_id}
                relationship={relationship}
                index={index}
                isLast={index === relationships.length - 1}
              />
            ))}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: { xs: 2, md: 3 }, pt: 0 }}>
        <Button onClick={onClose} variant="outlined" sx={{ borderRadius: 2 }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>

    {/* Delete Confirmation Dialog */}
    <Dialog
      open={deleteConfirmOpen}
      onClose={handleDeleteCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
        }
      }}
    >
      <DialogTitle sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        color: theme.palette.error.main,
      }}>
        <DeleteIcon />
        Delete Relationship?
      </DialogTitle>

      <DialogContent>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Are you sure you want to delete this relationship?
        </Typography>
        {relationshipToDelete && (
          <Box sx={{
            p: 2,
            backgroundColor: alpha(theme.palette.error.main, 0.05),
            borderRadius: 2,
            borderLeft: `3px solid ${theme.palette.error.main}`,
          }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              {relationshipToDelete.partner_name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatDate(relationshipToDelete.date_started)} - {formatDate(relationshipToDelete.date_ended)}
            </Typography>
          </Box>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          This will permanently delete the relationship and all associated data from your timeline.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button
          onClick={handleDeleteCancel}
          variant="outlined"
          disabled={deleting}
          sx={{ borderRadius: 2 }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleDeleteConfirm}
          variant="contained"
          color="error"
          disabled={deleting}
          sx={{ borderRadius: 2 }}
        >
          {deleting ? <CircularProgress size={20} /> : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default RelationshipTimeline;